import type { UploadParams } from '../type';
import { httpRequest } from '../utils/httpRequest';
import {
  assertBucketName,
  assertFile,
  createCancelHandle,
  createProgressSnapshot,
  getUploadConcurrency,
  getUploadPartSize,
  resolveUploadFileName,
  shouldUseMultipartUpload,
} from '../utils/utils';

interface ObsSdkInstance {
  createV4SignedUrlSync(options: Record<string, unknown>): {
    SignedUrl?: string;
  };
  uploadFile(
    options: Record<string, unknown>,
    callback: (err: unknown, result: unknown) => void
  ): void;
}

export async function upload(
  instance: unknown,
  params: UploadParams,
  bucketName?: string
): Promise<unknown> {
  const obs = instance as ObsSdkInstance;
  const file = assertFile(params.file);
  const resolvedBucketName = assertBucketName(bucketName);
  const fileName = resolveUploadFileName(params);
  const isMultipart = shouldUseMultipartUpload(file, params.maxSize);

  if (!isMultipart) {
    return uploadViaPresignedUrl(
      obs,
      file,
      fileName,
      resolvedBucketName,
      params
    );
  }

  return uploadViaMultipart(obs, file, fileName, resolvedBucketName, params);
}

async function uploadViaPresignedUrl(
  obs: ObsSdkInstance,
  file: File,
  fileName: string,
  bucketName: string,
  params: UploadParams
): Promise<unknown> {
  const abortHandle = createCancelHandle(params.onCancel);
  const signResult = obs.createV4SignedUrlSync({
    Bucket: bucketName,
    Key: fileName,
    Expires: 86400,
    Method: 'PUT',
  });

  if (!signResult?.SignedUrl) {
    throw new Error('ObjectStorage: failed to generate OBS presigned URL');
  }

  return httpRequest({
    url: signResult.SignedUrl,
    method: 'put',
    data: file,
    xhrInstance: abortHandle,
    onProgress: params.onUploadProgress,
  });
}

function uploadViaMultipart(
  obs: ObsSdkInstance,
  file: File,
  fileName: string,
  bucketName: string,
  params: UploadParams
): Promise<unknown> {
  const partSize = getUploadPartSize(params.partSize);
  const concurrency = getUploadConcurrency(params.queueLength);

  return new Promise((resolve, reject) => {
    obs.uploadFile(
      {
        Bucket: bucketName,
        Key: fileName,
        SourceFile: file,
        PartSize: partSize,
        TaskNum: concurrency,
        ProgressCallback(transferredAmount: number, totalAmount: number) {
          params.onUploadProgress?.(
            createProgressSnapshot(transferredAmount, totalAmount)
          );
        },
        ResumeCallback(resumeHook: unknown) {
          const hook = resumeHook as (() => void) | undefined;
          params.onCancel?.({ instance: null, cancel: () => hook?.() });
        },
      },
      (err, result) => {
        if (err) {
          reject(err);
          return;
        }
        resolve(result);
      }
    );
  });
}
